import { useState, useCallback, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "react-query";

import UserGroupPresenter from "./UserGroupPresenter";
import * as s from "../UsersStyled";
import TableButton from "components/atoms/buttons/TableButton";

import {
  BasePagination,
  PageTotalStatus,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { FaRegUser } from "react-icons/fa";
import RBACWrapper from "components/blocks/function/RBACWrapper";
import { useAuth } from "components/Auth";
import useApiError from "hook/useApiError";
import { useLocation, useOutletContext } from "react-router-dom";
import { isNull } from "lodash";
import { TableColumnsType } from "antd";
import {
  changeSortOrderAntd,
  onSortAntd,
} from "components/atoms/table/AntdTable";
import { ColumnType } from "antd/es/table";
import * as mediaQuery from "components/MediaQuery";
import * as MediaQuery from "components/MediaQuery";
import { baseQueryInfo, useQueryKey } from "utils/queryUtil";
import { useMenu } from "hook/useMenu";
import { getGroupList } from "api/uGroupAPI";
import { UserGroupListItem } from "api/interfaces/userInterface.interface";
import { UserPageOutletContext } from "../UserPage";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";

const initialQueryInfo: BasePagination = baseQueryInfo;

export default function UserGroupContainer(): JSX.Element {
  const location = useLocation();
  const {
    setStatus: setUserPageStatus,
    setButtonLabel,
    setTitleComponent,
    setOnClickBtn,
    setOnClickSearch,
    setKeyword,
  } = useOutletContext<UserPageOutletContext>();
  const { handleErrorNotify, lang } = useApiError();
  const queryClient = useQueryClient();
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const auth = useAuth();
  const isMobile = mediaQuery.useIsMobile();

  const [isAddModal, setIsAddModal] = useState<boolean>(false);
  const [isEditModal, setIsEditModal] = useState<string | null>(null);

  const [status, setStatus] = useState<PageTotalStatus>({
    totalTitle: "Total Groups",
    totalValue: 0,
  });

  const { queryNavigate } = useMenu({});

  useEffect(() => {
    const changeParam = new URLSearchParams(location.search);
    if (changeParam.size > 0) {
      setGroupQueryInfo((info) => {
        return {
          ...info,
          pageNum: isNull(changeParam.get("pageNum"))
            ? 0
            : Number(changeParam.get("pageNum")),
          pageLimit: isNull(changeParam.get("pageLimit"))
            ? 20
            : Number(changeParam.get("pageLimit")),
          keyword: isNull(changeParam.get("keyword"))
            ? ""
            : (changeParam.get("keyword") as string),
          sortType: isNull(changeParam.get("sortType"))
            ? "name"
            : (changeParam.get("sortType") as string),
          sortDirection: isNull(changeParam.get("sortDirection"))
            ? SORT_DIRECTION.ASC
            : (changeParam.get("sortDirection") as SORT_DIRECTION),
        };
      });
    } else {
      setGroupQueryInfo((info) => {
        return {
          ...initialQueryInfo,
        };
      });
    }
  }, [location]);

  const [pending, setPending] = useState<boolean>(true);
  const [groupQueryInfo, setGroupQueryInfo] = useState<BasePagination>({
    ...initialQueryInfo,
  });
  const queryKey = useQueryKey(groupQueryInfo);
  const onHeaderCell = (column: ColumnType<UserGroupListItem>) => {
    return {
      onClick: () => {
        if (column.key) {
          setGroupQueryInfo((info) => {
            return {
              ...info,
              sortType: column.key as string,
              sortDirection: onSortAntd(info.sortDirection),
            };
          });
        }
      },
    };
  };

  const isEditable = ({ enableDelete, hostEmail }: UserGroupListItem) => {
    if (enableDelete) {
      return selectedAccount.isAdminGroup || hostEmail === auth.user.email;
    }
    return selectedAccount.isAdminGroup;
  };
  // user roles
  const columns: TableColumnsType<UserGroupListItem> = [
    {
      title: "Name",
      render: (value, row) => (
        <s.Row isAdmin={!row.enableDelete}>
          <MediaQuery.Default>
            <div>
              <FaRegUser size={16} />
            </div>
          </MediaQuery.Default>
          {row.name}
        </s.Row>
      ),

      sorter: true,
      key: "name",
      sortOrder: changeSortOrderAntd(groupQueryInfo, "name"),
      onHeaderCell: onHeaderCell,
      defaultSortOrder: "ascend",
    },
    {
      title: "Host",
      dataIndex: "hostName",
      render: (value, row) => {
        const host = row.hostName
          ? `${row.hostName}${row.hostEmail ? ` (${row.hostEmail})` : ""}`
          : "";

        let text = "Provided by MyDW";

        if (row.enableDelete) {
          text = row.hostValidation ? host : `${host} - Deactivated User`;
        }

        return <s.Text isDeleted={!row.hostValidation}>{text}</s.Text>;
      },
      sorter: true,
      sortOrder: changeSortOrderAntd(groupQueryInfo, "hostName"),
      onHeaderCell: onHeaderCell,
      key: "hostName",
      width: "20%",
      responsive: ["md"],
    },
    {
      title: "Users",
      dataIndex: "hasUsers",
      render: (value, row) => value,
      align: "center",
      width: isMobile ? 100 : 200,
    },
    {
      title: "Description",
      render: (value, row) => row.description,
      sorter: true,
      sortOrder: changeSortOrderAntd(groupQueryInfo, "description"),
      onHeaderCell: onHeaderCell,
      key: "description",
      responsive: ["xl"],
      ellipsis: true,
      width: "30%",
    },
    // 마지막 버튼
    {
      title: "",
      width: isMobile ? 30 : 80,
      render: (value, row) => (
        <RBACWrapper requiredPermissions="manageUsersAndGroups">
          <s.Row className="table-button-wrapper">
            <span className="row-click-button">
              <TableButton
                label={isEditable(row) ? "Edit" : "View"}
                onClick={() => {
                  onModal("edit", row.groupId);
                }}
                data-testid="users-user-group-user-group-table-button-modal"
              />
            </span>
          </s.Row>
        </RBACWrapper>
      ),

      align: "right",
    },
  ];

  const onSuccessDelete = () => {
    queryNavigate({
      to: location.pathname,
      queryInfo: {
        ...initialQueryInfo,
      },
    });
  };
  const onModal = useCallback(
    (type: string, groupId?: string) => {
      console.log(groupId);
      if (type === "add") {
        if (isAddModal) {
          queryClient.invalidateQueries("group");
        }
        setIsAddModal(!isAddModal);
      }
      if (type === "edit") {
        setIsEditModal(groupId ?? null);
      }
    },
    [isAddModal, queryClient]
  );

  const { data, refetch } = useQuery(
    ["group", queryKey, selectedAccount.accountId],
    () =>
      getGroupList({
        payload: groupQueryInfo,
        accountId: selectedAccount.accountId,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (res: any) => {
        if (res.error !== 0 || res.result === undefined) {
          setGroupQueryInfo((info) => {
            return { ...initialQueryInfo };
          });
          setStatus((info) => {
            return { ...info, totalValue: 0 };
          });
          return;
        }
        setGroupQueryInfo((info) => {
          return { ...info, ...res.page };
        });

        setStatus({
          totalTitle: "Total Groups",
          totalValue: res.page.total,
        });
      },
      onError: (e: any) => {
        handleErrorNotify(e, lang.label.userGroup.notify.list.fail);
        setStatus((info) => {
          return { ...info, totalValue: 0 };
        });
      },
      onSettled: (e: any) => {
        setPending(false);
      },
    }
  );
  useStompDebouncedRefetch({
    stompKey: ["userGroupUpdated"] as StompDateSignalKey[],
    refetch: refetch,
  });

  const onChangePage = useCallback(
    (page: number, totalRows: number) => {
      console.log(page, totalRows);
      if (page !== groupQueryInfo.pageNum + 1) {
        queryNavigate({
          to: location.pathname,
          queryInfo: {
            pageLimit: groupQueryInfo.pageLimit,
            pageNum: page - 1,
            keyword: groupQueryInfo.keyword as string,
            sortType: groupQueryInfo.sortType,
            sortDirection: groupQueryInfo.sortDirection as SORT_DIRECTION,
          },
        });
        setPending(true);
        // setGroupQueryInfo((info) => {
        //   return { ...info, pageNum: (page - 1) as number };
        // });
      }
    },
    [
      groupQueryInfo.keyword,
      groupQueryInfo.pageLimit,
      groupQueryInfo.pageNum,
      groupQueryInfo.sortDirection,
      groupQueryInfo.sortType,
      location.pathname,
      queryNavigate,
    ]
  );

  const onClickSearch = useCallback(
    (keyword: string) => {
      if (groupQueryInfo.keyword !== keyword) {
        queryNavigate({
          to: location.pathname,
          queryInfo: {
            pageLimit: groupQueryInfo.pageLimit,
            pageNum: 0,
            keyword: keyword,
            sortType: groupQueryInfo.sortType,
            sortDirection: groupQueryInfo.sortDirection as SORT_DIRECTION,
          },
        });
      } else {
        refetch();
      }
      setPending(true);
    },
    [
      groupQueryInfo.keyword,
      groupQueryInfo.pageLimit,
      groupQueryInfo.sortDirection,
      groupQueryInfo.sortType,
      location.pathname,
      refetch,
      queryNavigate,
    ]
  );

  useEffect(() => {
    setTitleComponent("User Groups");
    setButtonLabel("Add Group");
    setOnClickBtn(() => () => onModal("add"));

    return () => {
      setOnClickBtn(undefined);
    };
  }, [onModal, setButtonLabel, setOnClickBtn, setTitleComponent]);

  useEffect(() => {
    setOnClickSearch(() => onClickSearch);
  }, [onClickSearch, setOnClickSearch]);

  useEffect(() => {
    setUserPageStatus(status);
  }, [setUserPageStatus, status]);

  useEffect(() => {
    setKeyword((groupQueryInfo.keyword as string) ?? "");
  }, [groupQueryInfo.keyword, setKeyword]);

  const groups: UserGroupListItem[] = useMemo(() => {
    if (data?.result && data.error === 0) {
      return data.result;
    }
    return [];
  }, [data]);

  return (
    <UserGroupPresenter
      columns={columns}
      data={groups}
      onModal={onModal}
      isAddModal={isAddModal}
      isEditModal={isEditModal}
      onChangePage={onChangePage}
      initialPending={pending}
      paginationInfo={groupQueryInfo}
      onSuccessDelete={onSuccessDelete}
    />
  );
}
